import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import {
    fetchConnectedAccounts,
    disconnectBankAccount,
    syncAccountTransactions,
    refreshAllAccounts,
    clearError,
} from '../../redux/slices/plaidSlice';
import classes from './Connections.module.css';
import PlaidLink from '../../components/PlaidLink';

const Connections = () => {
    const dispatch = useDispatch();
    const {
        connectedAccounts,
        loading,
        accountsLoading,
        syncingAccounts,
        disconnectingAccounts,
        error,
        lastSyncTime,
        isInitialized,
    } = useSelector((state) => state.plaid);

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    // Sample data for demo purposes - replace with actual data
    const demoAccounts = [
        {
            id: 1,
            account_name: 'Chase Checking',
            account_type: 'checking',
            account_id: 'VvBK',
            current_balance: 5020.00,
            currency: 'USD',
            bank_name: 'Chase',
            last_sync: '2025-01-15T10:30:00Z',
            status: 'active'
        },
        {
            id: 2,
            account_name: 'Savings Account',
            account_type: 'savings',
            account_id: 'xRQJ',
            current_balance: 12060.00,
            currency: 'USD',
            bank_name: 'Bank of America',
            last_sync: '2025-01-15T09:15:00Z',
            status: 'active'
        },
        {
            id: 3,
            account_name: 'HSA Account',
            account_type: 'investment',
            account_id: 'XZz6',
            current_balance: 6009.00,
            currency: 'USD',
            bank_name: 'Fidelity',
            last_sync: '2025-01-15T08:45:00Z',
            status: 'active'
        },
        {
            id: 4,
            account_name: 'Mortgage',
            account_type: 'loan',
            account_id: 'kWnm',
            current_balance: 56302.06,
            currency: 'USD',
            bank_name: 'Wells Fargo',
            last_sync: '2025-01-14T16:20:00Z',
            status: 'active'
        },
        {
            id: 5,
            account_name: 'Student Loan',
            account_type: 'loan',
            account_id: 'D7L5',
            current_balance: 65262.00,
            currency: 'USD',
            bank_name: 'Navient',
            last_sync: '2025-01-14T14:10:00Z',
            status: 'active'
        },
        {
            id: 6,
            account_name: '401k',
            account_type: 'investment',
            account_id: 'yvZL',
            current_balance: 23631.98,
            currency: 'USD',
            bank_name: 'Fidelity',
            last_sync: '2025-01-14T12:30:00Z',
            status: 'active'
        }
    ];

    // Use demo data if no real accounts are connected
    const displayAccounts = connectedAccounts.length > 0 ? connectedAccounts : demoAccounts;

    // Fetch connected accounts on component mount
    useEffect(() => {
        if (!isInitialized) {
            dispatch(fetchConnectedAccounts());
        }
    }, [dispatch, isInitialized]);

    // Clear error when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    // Handle successful bank connection
    const handlePlaidSuccess = (result) => {
        console.log('Bank connected successfully:', result);
        setShowSuccessMessage(true);
        setIsConnecting(false);

        // Hide success message after 5 seconds
        setTimeout(() => {
            setShowSuccessMessage(false);
        }, 5000);

        // Refresh accounts list
        dispatch(fetchConnectedAccounts());
    };

    // Handle Plaid Link exit (user cancels)
    const handlePlaidExit = (err, metadata) => {
        setIsConnecting(false);
        if (err) {
            console.log('Plaid Link exited with error:', err);
        } else {
            console.log('User cancelled Plaid Link');
        }
    };

    // Handle connecting new bank
    const handleConnectBank = () => {
        setIsConnecting(true);
        // PlaidLink component will handle the actual connection
    };

    // Handle removing a bank account
    const handleRemoveBank = async (bankId, bankName) => {
        if (window.confirm(`Are you sure you want to disconnect ${bankName}? This will remove all associated transaction data.`)) {
            try {
                await dispatch(disconnectBankAccount(bankId)).unwrap();
            } catch (error) {
                console.error('Failed to disconnect bank:', error);
            }
        }
    };

    // Handle syncing transactions for a specific account
    const handleSyncAccount = async (accountId) => {
        try {
            await dispatch(syncAccountTransactions({
                accountId,
                options: {
                    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Last 30 days
                }
            })).unwrap();
        } catch (error) {
            console.error('Failed to sync account:', error);
        }
    };

    // Handle refreshing all accounts
    const handleRefreshAll = async () => {
        try {
            await dispatch(refreshAllAccounts()).unwrap();
        } catch (error) {
            console.error('Failed to refresh accounts:', error);
        }
    };

    // Format currency
    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    // Format last sync time
    const formatLastSync = (timestamp) => {
        if (!timestamp) return 'Never';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        return date.toLocaleDateString();
    };

    const getAccountIcon = (type) => {
        switch (type) {
            case 'checking': return '🏦';
            case 'savings': return '💰';
            case 'investment': return '📈';
            case 'loan': return '🏠';
            case 'credit': return '💳';
            default: return '🏛️';
        }
    };

    const getAccountTypeClass = (type) => {
        switch (type) {
            case 'checking': return classes.checking;
            case 'savings': return classes.savings;
            case 'investment': return classes.investment;
            case 'loan': return classes.loan;
            case 'credit': return classes.credit;
            default: return classes.default;
        }
    };

    // Calculate totals
    const totalAssets = displayAccounts
        .filter(acc => ['checking', 'savings', 'investment'].includes(acc.account_type))
        .reduce((sum, acc) => sum + acc.current_balance, 0);

    const totalLiabilities = displayAccounts
        .filter(acc => acc.account_type === 'loan')
        .reduce((sum, acc) => sum + acc.current_balance, 0);

    // Loading state
    if (accountsLoading && !isInitialized) {
        return (
            <Layout>
                <div className={classes.connections}>
                    <div className={classes.loadingContainer}>
                        <div className={classes.spinner}></div>
                        <p>Loading your connected accounts...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={classes.connections}>
                {/* Success Message */}
                {showSuccessMessage && (
                    <div className={classes.successMessage}>
                        <div className={classes.successContent}>
                            <div className={classes.successIcon}>✓</div>
                            <div>
                                <div className={classes.successTitle}>Bank Connected!</div>
                                <div className={classes.successText}>Transactions are being synced</div>
                            </div>
                            <button
                                className={classes.closeSuccess}
                                onClick={() => setShowSuccessMessage(false)}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className={classes.errorMessage}>
                        <span className={classes.errorIcon}>⚠</span>
                        <span className={classes.errorText}>{error}</span>
                        <button
                            className={classes.closeError}
                            onClick={() => dispatch(clearError())}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Hero Header */}
                <div className={classes.heroHeader}>
                    <div className={classes.heroContent}>
                        <div className={classes.heroText}>
                            <h1 className={classes.heroTitle}>Connected Accounts</h1>
                            <p className={classes.heroSubtitle}>
                                Securely link all your financial accounts in one place for complete visibility
                            </p>
                            <div className={classes.securityFeatures}>
                                <div className={classes.securityFeature}>
                                    <span className={classes.featureIcon}>🔒</span>
                                    <span>Bank-level security</span>
                                </div>
                                <div className={classes.securityFeature}>
                                    <span className={classes.featureIcon}>🔄</span>
                                    <span>Real-time sync</span>
                                </div>
                            </div>
                        </div>

                        {/* Portfolio Overview */}

                    </div>
                    <div className={classes.heroDecorations}></div>
                </div>

                <div className={classes.mainContent}>
                    {/* Account Management */}
                    <div className={classes.managementSection}>
                        <div className={classes.managementCard}>
                            <div className={classes.managementHeader}>
                                <div>
                                    <h2 className={classes.managementTitle}>Account Management</h2>
                                    <p className={classes.managementSubtitle}>Add new accounts or manage existing connections</p>
                                    {lastSyncTime && (
                                        <p className={classes.lastSync}>
                                            Last updated: {formatLastSync(lastSyncTime)}
                                        </p>
                                    )}
                                </div>
                                <div className={classes.managementActions}>
                                    <button
                                        className={classes.refreshBtn}
                                        onClick={handleRefreshAll}
                                        disabled={loading}
                                        title="Refresh all accounts"
                                    >
                                        <span className={`${classes.refreshIcon} ${loading ? classes.spinning : ''}`}>
                                            🔄
                                        </span>
                                    </button>
                                    <PlaidLink
                                        onSuccess={handlePlaidSuccess}
                                        onExit={handlePlaidExit}
                                        className={classes.connectBtn}
                                    >
                                        {isConnecting ? (
                                            <div className={classes.connectingContent}>
                                                <div className={classes.connectingSpinner}></div>
                                                Connecting...
                                            </div>
                                        ) : (
                                            '+ Connect Bank Account'
                                        )}
                                    </PlaidLink>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Connected Banks */}
                    {displayAccounts.length > 0 ? (
                        <div className={classes.accountsSection}>
                            <h3 className={classes.sectionTitle}>Your Connected Accounts</h3>
                            <div className={classes.accountsGrid}>
                                {displayAccounts.map(account => (
                                    <div key={account.id} className={`${classes.accountCard} ${getAccountTypeClass(account.account_type)}`}>
                                        <div className={classes.accountContent}>
                                            <div className={classes.accountInfo}>
                                                <div className={classes.accountIcon}>
                                                    {getAccountIcon(account.account_type)}
                                                </div>
                                                <div className={classes.accountDetails}>
                                                    <h4 className={classes.accountName}>{account.account_name}</h4>
                                                    <div className={classes.accountMeta}>
                                                        <span className={classes.accountType}>
                                                            {account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1)}
                                                        </span>
                                                        <span className={classes.accountSeparator}>•</span>
                                                        <span className={classes.accountId}>****{account.account_id}</span>
                                                        <span className={classes.accountSeparator}>•</span>
                                                        <span className={classes.bankName}>{account.bank_name}</span>
                                                    </div>
                                                    <div className={classes.lastSyncInfo}>
                                                        Last sync: {formatLastSync(account.last_sync)}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={classes.balanceSection}>
                                                <div className={classes.balance}>
                                                    {formatCurrency(account.current_balance, account.currency)}
                                                </div>
                                                <div className={classes.balanceLabel}>Current Balance</div>
                                            </div>
                                        </div>

                                        <div className={classes.accountActions}>
                                            <button
                                                className={classes.syncBtn}
                                                onClick={() => handleSyncAccount(account.id)}
                                                disabled={syncingAccounts[account.id]}
                                                title="Sync transactions"
                                            >
                                                {syncingAccounts[account.id] ? (
                                                    <span className={classes.spinner}></span>
                                                ) : (
                                                    '🔄'
                                                )}
                                            </button>

                                            <button
                                                className={classes.removeBtn}
                                                onClick={() => handleRemoveBank(account.id, account.account_name)}
                                                disabled={disconnectingAccounts[account.id]}
                                                title="Disconnect bank account"
                                            >
                                                {disconnectingAccounts[account.id] ? (
                                                    <span className={classes.spinner}></span>
                                                ) : (
                                                    '✕'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        /* Empty State */
                        <div className={classes.emptyState}>
                            <div className={classes.emptyIcon}>🏦</div>
                            <h3 className={classes.emptyTitle}>No banks connected yet</h3>
                            <p className={classes.emptyDescription}>
                                Connect your first bank account to start tracking your finances and get personalized insights powered by AI.
                            </p>
                            <PlaidLink
                                onSuccess={handlePlaidSuccess}
                                onExit={handlePlaidExit}
                                className={classes.connectFirstBtn}
                            >
                                Connect Your First Bank
                            </PlaidLink>
                        </div>
                    )}

                    {/* Security & Info Section */}
                    {displayAccounts.length > 0 && (
                        <div className={classes.infoSection}>
                            <div className={classes.infoGrid}>
                                <div className={classes.infoCard}>
                                    <div className={classes.infoHeader}>
                                        <div className={classes.infoIconWrapper}>
                                            <span className={classes.infoIcon}>🔒</span>
                                        </div>
                                        <h4 className={classes.infoTitle}>Your data is secure</h4>
                                    </div>
                                    <p className={classes.infoDescription}>
                                        We use bank-level encryption and never store your login credentials.
                                        All connections are read-only and managed through Plaid.
                                    </p>
                                </div>
                                <div className={classes.infoCard}>
                                    <div className={classes.infoHeader}>
                                        <div className={classes.infoIconWrapper}>
                                            <span className={classes.infoIcon}>🔄</span>
                                        </div>
                                        <h4 className={classes.infoTitle}>Auto-sync transactions</h4>
                                    </div>
                                    <p className={classes.infoDescription}>
                                        Your transactions are automatically synchronized daily.
                                        You can also manually refresh any account at any time.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Connections;